/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import * as React from 'react';
import { Box, Typography } from '@mui/material';
import type { Feature } from 'geojson';
import { atom } from 'jotai';
import { useAtomValue } from 'jotai';
import countyDict from './Incident_page_dictionaries/County_dict.json';
import communicationsDict from './Incident_page_dictionaries/Communication_dict.json';
import commoditiesDict from './Incident_page_dictionaries/Commodities_dict.json';
import energyDict from './Incident_page_dictionaries/Energy_dict.json';
import foodWaterDict from './Incident_page_dictionaries/FoodWaterShelter_dict.json';
import healthDict from './Incident_page_dictionaries/HealthAndMedical_dict.json';
import nonResidentialDict from './Incident_page_dictionaries/NonResidentialStructures_dict.json';
import populationExposureDict from './Incident_page_dictionaries/PopulationExposure_dict.json';
import residentialStructuresDict from './Incident_page_dictionaries/ResidentialStructures_dict.json';
import transportationDict from './Incident_page_dictionaries/Transportation_dict.json';
import grantsDict from './Incident_page_dictionaries/Grants_dict.json';
import wowDict from './Incident_page_dictionaries/Wow_dict.json';
import structuresDict from './Incident_page_dictionaries/Structures_dict.json';
import { getRankColor, primaryKeyForSection } from '../Post/PostLayer';
import { useMatch } from 'react-router-dom';
import { IncidentSection } from '../pages/IncidentPage';
import { isTingLayerId } from '../ting/TingOutagesLayer';
import { leafKeyForLayerId } from '../map/layerControls/layerRegistry';
import { useStructureDetail } from '../map/layers/StructuresLayer';

const RANK_KEYS: string[] = [
  'priority_rank',
  'exposurescore',
  'exposurescoreformap',
  'impactscore',
  'exposurescore_allstructures',
  'hf_np_exposure',
  'hf_hp_exposure',
  'nf_hp_exposure',
  'nf_np_impact',
  'exposurescore_agriculturalru',
  'exposurescore_commercialoff',
  'exposurescore_commercialret',
  'exposurescore_exemptgovernm',
  'exposurescore_heavyindustria',
  'exposurescore_industrialgen',
  'exposurescore_miscellaneous',
  'exposurescore_recreational',
  'exposurescore_residential',
  'exposurescore_vacantland',
].map(k => k.toLowerCase());

const sectionHighlights: Record<
  IncidentSection | 'canopy' | 'structures' | 'ting-outages' | 'nifc-perimeter',
  string[]
> = {
  canopy: ['event_name', 'ef_rank', 'structures_exposed'],
  'nifc-perimeter': ['createdate', 'structures_exposed'],
  'ting-outages': [
    'recovered_percentage',
    'power_state',
    'vendor_start',
    'vendor_modified',
    'structures_exposed',
    'residential_struc_exposed',
    'observed_at',
    'run_time',
  ],
  structures: [
    'BUILD_ID',
    'OCC_CLS',
    'PRIM_OCC',
    'SEC_OCC',
    'PROP_ADDR',
    'HEIGHT',
    'SQMETERS',
    'SQFEET',
  ],
  population: [
    'LS_NIGHT_POP',
    'HOUSINGUNITS',
    'POP65OVER',
    'LEP',
    'HOUSEHOLDSWITHFS',
    'HOUSEHOLDSWITHPA',
  ],
  residential: ['residential', 'totalnumberofrelevantstructure'],
  nonResidentialStructures: [
    'AGRICULTURALRU',
    'COMMERCIALOFF',
    'COMMERCIALRET',
    'EXEMPTGOVERNM',
    'HEAVYINDUSTRIA',
    'INDUSTRIALGEN',
    'RECREATIONAL',
  ],
  // foodWaterShelter: [
  //   'SHELTERCAPACITYPERCENT',
  //   'BOILWATERAFFECTEDPOPULATION',
  //   'POPINOPENSHELTERS',
  //   'SHELTERTOTALCAPACITY',
  //   'SHELTERSCLOSED',
  //   'SHELTERSOPEN',
  //   'COUNTIESSJ_SHELTERCAP_FIPS',
  //   'COUNTIESSJ_SHELTERCAP_FREQUENCY',
  //   'COUNTIESSJ_SHELTERCAP_FREQUENC',
  //   'COUNTIESSJ_POPSERVED_SUM_POPUL',
  // ],
  // healthMedical: [
  //   'DIALYSIS_CENTER',
  //   'HOSPITALS',
  //   'NURSING_HOMES',
  //   'URGENT_CARE',
  // ],
  // transportation: ['ROAD_CLOSED', 'TOTALNUMALERTS'],
  // energy: [
  //   'APPROXIMATENUMBEROFCUSTOMERSNOP',
  //   'HAS_FUEL__NO_POWER',
  //   'NO_FUEL__HAS_POWER',
  //   'NO_FUEL__NO_POWER',
  //   'has_fuel__has_power',
  // ],
  communications: [
    'CELL_SITES_OUT',
    'CELL_SITES_SERVED',
    'CELL_SITES_OUT_DUE_TO_POWER',
    'CELL_SITES_OUT_DUE_TO_TRANSPOR',
    'CELL_SITES_OUT_DUE_TO_DAMAGE',
  ],
  // commodities: [
  //   'VULNERABLEPOP',
  //   'PLASTICSHEETREQS',
  //   'PEOPLEINNEED',
  //   'WATERREQ',
  //   'MEALREQ',
  //   'miscellaneous',
  //   'vacantland',
  //   'rpl_themes',
  //   'f_total',
  //   'hdrreqs',
  //   'dmereqs',
  //   'medicalsuppliesreqs',
  //   'infanttodkitreqs',
  //   'totalcotreqs',
  //   'normalcotreqs',
  //   'edccotreqs',
  //   'blanketreqs',
  //   'daysofresponse',
  //   'tpmr',
  //   'cmr',
  //   'smr',
  //   'edcotreqs',
  //   'infanttodkitsreqs',
  // ],
  declarations: [
    'is_affected',
    'is_designated',
    'designation_type',
    'Disasters_last_12_months',
    'Exposed_Residences',
    'HMA_Grant_Count',
    'HMA_Grant_Total_Cost',
    'HMA_Grant_Federal_Share_Total_C',
    'Disaster_Declarations',
    'Percent_home_insurance',
  ],
};

const subcategories: {
  label: string;
  keys: string[];
}[] = [
  {
    label: 'Highlights',
    keys: [
      ...RANK_KEYS,
      'totalnumfacilities',
      'PERCENT_OUT',
      'POPULATIONAFFECTED',
      'Event',
      'LASTUPDATEDON',
      'COMMENTS',
    ],
  },
  {
    label: 'POST priorities',
    keys: [
      'ef_rank',
      'threat',
      'category',
      'threatlevel',
      'threatdescription',
      'event_name',
      'priority_rank',
      'final_rank',
      'run_time',
      'quantile_sum',
      'effective',
      'expires',
      'event_id',
      'datasource',
      'time_category',
      'ERO_Day_1',
      'Watches_Warnings',
      'FEMA_CRCI_County_Percentile',
      'minelev',
      'maxelev',
      'relief',
      'maxslope',
    ],
  },
  {
    label: 'Demographics',
    keys: [
      'age65',
      'amalask',
      'lep',
      'land_scan_day_pop',
      'land_scan_night_pop',
      'nolaborforce16',
      'per_amalask',
      'per_dhisnfs',
      'per_hdisfs',
      'per_hfsnap',
      'per_hpa',
      'per_hpov',
      'per_humb',
      'per_lep',
      'per_nolaborforce16',
      'per_pop65',
      'per_unemp16',
      'sum_lep',
      'unemp16',
      'ls_day_pop',
      'ls_night_pop',
      'pop',
      'population',
      'pop65over',
      'pop16overunemployed',
      'pop16overnotinlaborforce',
      'popamindianalaskan',
      'Percent_receving_SSI',
      'Per_capita_income',
      'Percent_AIAN',
      'Percent_age_under_18',
      'Percent_age_65_and_over',
      'Percent_below_poverty_level',
      'Percent_households_food_stamps',
      'Percent_25_and_over_bachelors_d',
      'Percent_25_and_over_high_school',
      'Percent_uninsured_health',
      'Percent_with_disability',
      'Percent_speak_English_less_than',
      'Percent_unemployment_16_years_a',
      'Percent_not_in_labor_force_16_y',
    ],
  },
  {
    label: 'Location details',
    keys: [
      'usng5km',
      'usng1km',
      'usng_resolution',
      'state',
      'county',
      'fips',
      'state_fips',
      'households',
      'housingunits',
      'householdsinpoverty',
      'householdsdsfs',
      'householdsdsnofs',
      'householdswithfs',
      'householdswithpa',
      'housingunitsmobilehomes',
      'county_cell_tower_count',
      'county_dialysis_count',
      'county_gas_station_count',
      'county_hospital_count',
      'county_name',
      'county_nursing_home_count',
      'county_shelter_count',
      'hdisfs',
      'hdisnfs',
      'hfsnap',
      'hholds',
      'hpa',
      'hpov',
      'humb',
      'hunits',
      'NAME',
      'STATE_NAME',
      'State_Tax_Revenue_in_millions_o',
      'State_GDP_in_millions_of_dollar',
      'State_Total_Taxable_Resources_i',
      'Percent_housing_units_mortgage',
      'Percent_housing_owner_occupied',
      'Percent_households_public_assis',
      'Mean_household_income',
      'Median_household_income',
      'COUNTIES_NAME',
      'COUNTIES_STATE_NAME',
      'COUNTIES_CNTY_FIPS',
      'COUNTIES_POPULATION',
      'COUNTIES_POP2010',
      'COUNTIESSJ_POPSERVED_SUM_POPULA',
      'structures_exposed',
      'county_intersect',
      'state_intersect',
      'fips_intersect',
      'BUILD_ID',
      'OCC_CLS',
      'PRIM_OCC',
      'SEC_OCC',
      'PROP_ADDR',
      'PROP_CITY',
      'PROP_ST',
      'PROP_ZIP',
      'OUTBLDG',
      'HEIGHT',
      'SQMETERS',
      'SQFEET',
      'H_ADJ_ELEV',
      'L_ADJ_ELEV',
      'FIPS',
      'CENSUSCODE',
      'PROD_DATE',
      'SOURCE',
      'USNG',
      'LONGITUDE',
      'LATITUDE',
      'STATE_FIPS',
      'REMARKS',
      'exposure_binary',
      'exposure_quantity',
      'exposure_source',
      'exposure_binary_list',
      'exposure_quantity_list',
      'exposure_source_list',
      'damage_assessment_category',
      'damage_assessment_source',
    ],
  },
];

// const hiddenProperties = [
//   'id',
//   'geoid',
//   'globalid',
//   'geoid_1644434122041',
//   'fips_1',
//   'OBJECTID',
//   'OBJECTID_1',
//   'SHAPE__Area',
//   'SHAPE__Length',
//   'creationdate',
//   'creator',
//   'editdate',
//   'editor',
//   'date_',
//   'join_count',
//   'target_fid',
//   'cnty_fips',
//   'state_abbr',
//   'county_st_affected',
//   'county_state',
//   'affected_counties',
//   'countycaps',
//   'fema_regio',
//   'totalnumberofrelevantstructu_1',
//   'risk_score_max',
//   'hrcn_risks_max',
//   'avln_risks_max',
//   'cfld_risks_max',
//   'cwav_risks_max',
//   'drgt_risks_max',
//   'erqk_risks_max',
//   'hail_risks_max',
//   'hwav_risks_max',
//   'istm_risks_max',
//   'lnds_risks_max',
//   'ltng_risks_max',
//   'rfld_risks_max',
//   'swnd_risks_max',
//   'trnd_risks_max',
//   'tsun_risks_max',
//   'vlcn_risks_max',
//   'wfir_risks_max',
//   'wntw_risks_max',
//   'usng5km_1',
//   'residentialc',
//   'agriculturalruc',
//   'commercialoffc',
//   'commercialretc',
//   'exemptgovernmc',
//   'heavyindustriac',
//   'industrialgenc',
//   'miscellaneousc',
//   'recreationalc',
//   'vacantlandc',
//   'countiessj_sheltercap_objectid',
//   'lastupdatedon1',
// ].map(k => k.toLowerCase());

export type SelectedFeature = Feature & { section?: string };
export const selectedFeatureAtom = atom<SelectedFeature | null>(null);

/** Case-insensitive extraction of ArcGIS object ID from feature properties. */
export function getFeatureObjectId(
  properties: Record<string, unknown> | null | undefined
): number | undefined {
  if (!properties) return undefined;
  const key = Object.keys(properties).find(k => k.toLowerCase() === 'objectid');
  if (key == null) return undefined;
  const val = Number(properties[key]);
  return Number.isFinite(val) ? val : undefined;
}

export const commonDictionary = {
  ERO_Day_1: 'ERO Day 1',
  Watches_Warnings: 'Watches Warnings',
  FEMA_CRCI_County_Percentile: 'FEMA CRCI County Percentile',
  minelev: 'Minimum Elevation',
  maxelev: 'Maximum Elevation',
  relief: 'Relief',
  maxslope: 'Maximum Slope',
  canopy_published_date: 'Canopy Published Date',
  Category: 'Category',
  category: 'Category',
  CommercialOfficeImpacts: 'Commercial Office Impact',
  county_intersect: 'County intersect',
  county: 'County',
  DataSource: 'Data Source',
  datasource: 'Data Source',
  ef_rank: 'Enhanced Fujita Rank',
  effective: 'Effective',
  event_id: 'Grouped IPAWS event',
  event_name: 'Event Name',
  event: 'IPWAS event type',
  expires: 'Expires',
  ExposureScore: 'Exposure Score',
  final_rank: 'Unscaled Rank',
  fips_intersect: 'FIPS intersect',
  fips: 'County FIPS code',
  households: 'Number of Households',
  householdsdsfs: 'Number of Households on disability and food stamps',
  householdsdsnofs: 'Number of Households on disability no food stamps',
  householdsinpoverty: 'Number of Households in poverty',
  householdswithfs: 'Number of Households with food stamps/SNAP',
  householdswithpa: 'Number of Households with public assistance',
  housingunits: 'Number of Housing units',
  housingunitsmobilehomes: 'Number of Housing units that are mobile homes',
  ImpactScore: 'Impact Score',
  IncidentName: 'Incident Name',
  incidentname: 'Incident Name',
  LastUpdatedOn: 'Last Updated On',
  lastupdatedon: 'Last Updated On',
  lep: 'Population with Limited English Proficiency',
  ls_day_pop: 'LandScan Daytime Population',
  ls_night_pop: 'LandScan Nighttime Population',
  PercentOut: 'Percent Out',
  pop: 'Population',
  pop16overnotinlaborforce: 'Population 16+ not in labor force',
  pop16overunemployed: 'Population age 16+ and unemployed',
  pop65over: 'Population age 65 and over',
  popamindianalaskan: 'Population American Indian or Alaska Native',
  priority_rank: 'POST Priority',
  quantile_sum: 'Rank Sum',
  RankPotentialPowerOutageImpact: 'Rank Potential Power Outage Impact',
  run_time: 'POST run time',
  state_intersect: 'State intersect',
  state: 'State',
  structures_exposed: 'Structures Exposed',
  Threat: 'Threat',
  threat: 'Threat',
  ThreatDescription: 'Threat Description',
  threatdescription: 'Threat Description',
  ThreatLevel: 'Threat Level',
  threatlevel: 'Threat Level',
  time_category: 'Time Category',
  usng5km: '5km USNG cell',
  created_at: 'Created At',
  Shape__Area: 'Shape Area',
  Shape__Length: 'Shape Length',
  kml_id: 'KML ID',
  BUILD_ID: 'Build ID',
  OCC_CLS: 'Occupancy Class',
  PRIM_OCC: 'Primary Occupancy',
  SEC_OCC: 'Secondary Occupancy',
  PROP_ADDR: 'Property Address',
  PROP_CITY: 'Property City',
  PROP_ST: 'Property State',
  PROP_ZIP: 'Property Zip Code',
  OUTBLDG: 'Outbuilding',
  HEIGHT: 'Height',
  SQMETERS: 'Square Meters',
  SQFEET: 'Square Feet',
  H_ADJ_ELEV: 'Height Adjusted Elevation',
  L_ADJ_ELEV: 'Low Adjusted Elevation',
  FIPS: 'FIPS Code',
  CENSUSCODE: 'Census Code',
  PROD_DATE: 'Production Date',
  SOURCE: 'Source',
  USNG: 'US National Grid',
  LONGITUDE: 'Longitude',
  LATITUDE: 'Latitude',
  IMAGE_NAME: 'Image Name',
  IMAGE_DATE: 'Image Date',
  VAL_METHOD: 'Validation Method',
  REMARKS: 'Remarks',
  UUID: 'UUID',
  STATE_FIPS: 'State FIPS Code',
};

// Section key -> property key -> human label. Every entry is one of the
// imported JSON dictionaries or a commonDictionary override, so the value type
// is uniform; consumers index it with a section id from the route and a
// property name from the feature, both plain strings.
export const postKeyToLabel: Record<string, Record<string, string>> = {
  county: countyDict,
  canopy: {
    ...commonDictionary,
    structures_exposed: 'Structures Exposed in Swath',
  },
  'nifc-perimeter': {
    ...commonDictionary,
    createdate: 'Creation Date',
    structures_exposed: 'Structures Exposed in Perimeter',
    run_time: 'Snapshot Time',
  },
  'ting-outages': {
    ...commonDictionary,
    event_id: 'Outage Event ID',
    recovered_percentage: 'Estimated Recovery',
    power_state: 'Power State',
    vendor_start: 'Outage Start (Vendor)',
    vendor_modified: 'Last Modified (Vendor)',
    residential_struc_exposed: 'Residential Structures Exposed',
    observed_at: 'Last Observed',
    run_time: 'Snapshot Time',
  },
  communications: communicationsDict,
  commodities: commoditiesDict,
  energy: energyDict,
  foodWaterShelter: foodWaterDict,
  healthMedical: healthDict,
  transportation: transportationDict,
  residential: residentialStructuresDict,
  population: populationExposureDict,
  nonResidentialStructures: nonResidentialDict,
  declarations: grantsDict,
  'wow-perimeter': wowDict,
  'wow-forecast-perimeter': wowDict,
  'wow-observed-perimeter': wowDict,
  structures: structuresDict,
};

export const structureSubcategories = [
  {
    label: 'Building Information',
    keys: ['UUID', 'BUILD_ID', 'B_CODE', 'OCC_CLS', 'PRIM_OCC', 'SEC_OCC', 'OUTBLDG'],
  },
  {
    label: 'Location',
    keys: [
      'PROP_ADDR',
      'PROP_CITY',
      'PROP_ST',
      'PROP_ZIP',
      'FIPS',
      'STATE_FIPS',
      'CENSUSCODE',
      'USNG',
      'LONGITUDE',
      'LATITUDE',
    ],
  },
  {
    label: 'Physical Characteristics',
    keys: ['HEIGHT', 'SQMETERS', 'SQFEET', 'H_ADJ_ELEV', 'L_ADJ_ELEV'],
  },
  {
    label: 'Population Estimates',
    keys: ['POP_MEDIAN', 'POP_CI95_LOWER', 'POP_CI95_UPPER'],
  },
  {
    label: 'Metadata',
    keys: ['PROD_DATE', 'SOURCE', 'IMAGE_NAME', 'IMAGE_DATE', 'VAL_METHOD', 'REMARKS'],
  },
];

export const FeatureInfo = (props: { feature: Feature; layerId: string }) => {
  const { feature, layerId } = props;
  const selectedFeature = useAtomValue(selectedFeatureAtom);
  const isCanopy = feature.properties?.canopy_published_date != null;
  const isStructure = layerId.includes('usa-structures');
  // Keyed off the layer id (like structures), not a property sniff: every Ting
  // column except event_id/vendor_modified is nullable, and without this
  // branch the popup falls through to commonDictionary, which maps event_id to
  // the IPAWS label.
  const isTing = isTingLayerId(layerId);
  // The live perimeter layer (NIFC/NifcLayer.tsx) and the historical one
  // (built by useWeatherEventsLayer) both carry nifc_bounds columns and both
  // resolve to the registry's perimeter leaf, which owns their id patterns.
  // Keyed off the layer id like structures and Ting: the NIFC POST grid also
  // carries incidentname, so a property sniff would claim its cells too.
  const isNifcPerimeter = leafKeyForLayerId(layerId) === 'nifc-extent';

  // A footprint carries only the columns its layer draws with; the row this
  // popup lists is fetched by primary key while the footprint is shown.
  const structureId = isStructure ? feature.properties?.globalid : undefined;
  const structureDetail = useStructureDetail(
    typeof structureId === 'string' ? structureId : undefined
  );

  const match = useMatch({
    path: ':incidentId/:sectionId/:viewType/:attributeId?',
  });

  const sectionKey = isCanopy
    ? 'canopy'
    : isStructure
      ? 'structures'
      : isTing
        ? 'ting-outages'
        : isNifcPerimeter
          ? 'nifc-perimeter'
          : ((match?.params.sectionId ?? '') as IncidentSection);

  const primaryKey = useAtomValue(primaryKeyForSection(sectionKey ?? ''));

  // Prefer enriched properties when they match the current feature.
  const properties = React.useMemo((): Record<string, unknown> | null => {
    if (isStructure) {
      return structureDetail.state === 'loaded'
        ? { ...feature.properties, ...structureDetail.properties }
        : feature.properties;
    }
    const featureOid = getFeatureObjectId(feature.properties);
    const enrichedOid = getFeatureObjectId(selectedFeature?.properties);
    if (featureOid != null && enrichedOid != null && featureOid === enrichedOid) {
      return selectedFeature!.properties;
    }
    return feature.properties;
  }, [feature.properties, selectedFeature, isStructure, structureDetail]);

  // PostGIS serves lowercase column names (AGOL served OCC_CLS/PROP_ADDR), while
  // the highlight lists, the subcategory key lists and the label dictionaries
  // below are all still written in the AGOL casing. Every read of a feature
  // property therefore goes through one lowercased copy rather than each section
  // spelling both cases -- which is how the structures section came to render no
  // rows at all: its keys are uppercase and it was the one section reading the
  // raw properties.
  const attributes = React.useMemo(() => lowercaseKeys(properties), [properties]);

  // sectionKey comes from the URL route param, so a value like `constructor`
  // would otherwise resolve up the prototype chain to a function that the
  // spread below throws on.
  const highlights = Object.hasOwn(sectionHighlights, sectionKey)
    ? sectionHighlights[sectionKey]
    : undefined;

  // Same guard for the label dictionary: an unknown sectionKey carries no
  // labels (each property renders under its own key), and the own-property
  // check keeps a URL value like `constructor` from resolving up the prototype
  // chain to a function.
  const sectionLabels: Record<string, string> = Object.hasOwn(postKeyToLabel, sectionKey)
    ? postKeyToLabel[sectionKey]
    : {};

  // A highlighted key is promoted into the first group and withdrawn from every
  // group that also lists it (structures_exposed under "Location details" for
  // canopy and the NIFC perimeter, run_time under "POST priorities" for Ting),
  // so a column renders once, not under two headings.
  const subcats = React.useMemo(() => {
    if (isStructure) {
      return structureSubcategories;
    }

    const highlighted = new Set((highlights ?? []).map(key => key.toLowerCase()));
    const [first, ...rest] = subcategories.map(subcat => ({
      ...subcat,
      keys: subcat.keys.filter(key => !highlighted.has(key.toLowerCase())),
    }));

    return [{ ...first, keys: [...(highlights ?? []), ...first.keys] }, ...rest];
  }, [highlights, isStructure]);

  if (properties == null) {
    return null;
  }

  // The accent colour comes from the section's rank column; a column holding
  // anything but a number carries no rank.
  const rank = properties[primaryKey];

  return (
    <Box
      sx={{
        pl: 1,
        pr: 1,
        minWidth: 354,
        backgroundColor: 'rgb(255, 255, 255)',
        borderLeft: `4px solid rgb(${getRankColor(typeof rank === 'number' ? rank : undefined)})`,
      }}
    >
      <Typography variant="caption" sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{renderable(attributes.event)}</span>
      </Typography>
      <Typography variant="h6">
        {isStructure ? (
          <>
            {attributes.prop_addr && `${attributes.prop_addr}, `}
            {attributes.prop_city && `${attributes.prop_city}, `}
            {renderable(attributes.prop_st)} {renderable(attributes.prop_zip)}
          </>
        ) : isNifcPerimeter ? (
          renderable(attributes.incidentname)
        ) : (
          <>
            {renderable(attributes.county ?? attributes.county_name ?? attributes.county_state)}{' '}
            {['declarations', 'communications'].includes(sectionKey) && (
              <>
                {renderable(attributes.name)}, {renderable(attributes.state_name)}
              </>
            )}
          </>
        )}
      </Typography>
      {isStructure && structureDetail.state === 'loading' && (
        <Typography variant="caption" sx={{ display: 'block' }}>
          Loading building details...
        </Typography>
      )}
      {isStructure && structureDetail.state === 'error' && (
        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
          Building details failed to load
        </Typography>
      )}
      <Typography
        variant="subtitle1"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {renderable(attributes.usng5km) && <span>USNG: {renderable(attributes.usng5km)}</span>}
        {renderable(attributes.usng1km) && <span>USNG: {renderable(attributes.usng1km)}</span>}
        {renderable(attributes.usng) && <span>USNG: {renderable(attributes.usng)}</span>}

        {(attributes.fips ?? attributes.counties_cnty_fips ?? attributes.geoid) != null && (
          <span>
            FIPS: {renderable(attributes.fips ?? attributes.counties_cnty_fips ?? attributes.geoid)}
          </span>
        )}
      </Typography>

      <Box sx={{ mt: 1 }}>
        {subcats.map((subcat, index) => {
          const keys = subcat.keys.filter(subkey => attributes[subkey.toLowerCase()] != null);

          if (keys?.length === 0) {
            return null;
          }

          return (
            <details
              key={index}
              open={index === 0}
              style={{
                borderBottom: '1px solid rgb(207, 226, 240)',
              }}
            >
              <summary
                style={{
                  padding: '8px',
                }}
              >
                {subcat.label}
              </summary>
              <Box key={subcat.label}>
                <Box>
                  {keys.map(key => {
                    const value = formatValue(key, attributes[key.toLowerCase()]);

                    // sectionKey is 'structures' for a picked footprint, so the
                    // structures dictionary is resolved here like every other
                    // section's -- no separate lookup.
                    const label = lowercaseKeys(sectionKey ? sectionLabels : commonDictionary)[
                      key.toLowerCase()
                    ];

                    return (
                      <Typography
                        key={key}
                        variant="body2"
                        sx={{
                          borderBottom: '1px solid rgb(207, 226, 240)',
                          pt: 0.5,
                          pb: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          span: {
                            display: 'flex',
                            alignItems: 'center',
                            maxWidth: '75%',
                            padding: '0 8px',
                            svg: {
                              marginRight: 1,
                            },
                          },
                        }}
                      >
                        <span>{label ?? key}</span>
                        <span>{value}</span>
                      </Typography>
                    );
                  })}
                </Box>
              </Box>
            </details>
          );
        })}
      </Box>
    </Box>
  );
};

/**
 * The same record keyed by lowercased key. Generic in the value type so a label
 * dictionary stays a dictionary of strings while feature properties -- any
 * PostGIS column, any type -- stay unknown.
 */
const lowercaseKeys = <T,>(obj: Record<string, T> | null | undefined): Record<string, T> => {
  if (obj == null) {
    return {};
  }
  return Object.keys(obj).reduce<Record<string, T>>((acc, key) => {
    acc[key.toLowerCase()] = obj[key];
    return acc;
  }, {});
};

/**
 * A property value React can render. Feature properties carry whatever their
 * column holds, and only primitives are valid children.
 */
const renderable = (value: unknown): string | number | null =>
  typeof value === 'string' || typeof value === 'number' ? value : null;

const coordinateFields = new Set(['latitude', 'longitude']);

const knownIdFields = new Set([
  // Existing application constants
  'build_id',
  'fips', // Federal Information Processing Standards ID
  'state_fips', // State FIPS ID
  'censuscode', // Census ID
  'uuid', // USA Structures unique identifier

  // General ID fields
  'id',
  '_id',
  'fid',
  'objectid',
  'featureid',
  'identifier',

  // Domain-specific IDs
  'messageid', // IPAWS message ID
  'incidentid', // Incident tracking
  'alertid', // Alert system ID
  'eventid', // Event tracking
  'structureid', // Building/structure ID
  'facilityid', // Facility reference
  'boundaryid', // Geographic boundary ID
  'polygonid', // Geographic shape ID
  'zoneid', // Zone reference
  'areaid', // Area reference
  'locationid', // Location reference
]);

// Timestamp columns whose names do not contain "date", so the generic check in
// formatValue misses them and they would render as raw ISO strings.
const timestampFields = new Set(['vendor_start', 'vendor_modified', 'observed_at', 'run_time']);

// Numeric columns holding a 0..1 fraction that reads as a percentage.
const fractionPercentFields = new Set(['recovered_percentage']);

// Counts of people, households and housing units: the fourteen census
// attributes plus the two LandScan populations. The POST workflows and FEMA's
// incident layers apportion each tract's total across the grid cells it
// covers, so a stored value can be a fraction of a person, and the LandScan
// figures are modelled populations the pipeline stores unrounded. The
// exposure stats tiles round all of them with Math.round; the popup shows
// the same whole numbers. Math.round takes an exact half up where the
// workflows round it to even, which only a legacy fractional value can show.
const wholeCountFields = new Set([
  'pop',
  'ls_day_pop',
  'ls_night_pop',
  'households',
  'housingunits',
  'pop65over',
  'pop16overunemployed',
  'pop16overnotinlaborforce',
  'popamindianalaskan',
  'householdsinpoverty',
  'householdsdsfs',
  'householdsdsnofs',
  'householdswithfs',
  'householdswithpa',
  'housingunitsmobilehomes',
  'lep',
]);

export function formatValue(key: string, value: unknown): React.ReactNode {
  if (value instanceof Date) {
    return value.toLocaleString();
  }
  if (key.toLowerCase().includes('date')) {
    return new Date(value as string | number).toLocaleString();
  }
  if (
    timestampFields.has(key.toLowerCase()) &&
    (typeof value === 'string' || typeof value === 'number')
  ) {
    return new Date(value).toLocaleString();
  }
  if (typeof value === 'number' && fractionPercentFields.has(key.toLowerCase())) {
    return value.toLocaleString(undefined, { style: 'percent', maximumFractionDigits: 1 });
  }
  if (typeof value === 'number' && wholeCountFields.has(key.toLowerCase())) {
    return Math.round(value).toLocaleString();
  }

  if (isIdField(key)) {
    return String(value);
  }

  if (typeof value === 'number') {
    if (coordinateFields.has(key.toLowerCase())) {
      return String(value);
    }
    return value.toLocaleString();
  }
  if (typeof value === 'string') {
    return value;
  }
  return String(value);
}

export function isIdField(key: string): boolean {
  return knownIdFields.has(key.toLowerCase());
}
